import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getNotesSummaryPage, getNotesTags, getSharedNotes, deleteNote,
  shareNote, emailNoteToRecipients, duplicateNote,
} from "./NoteService";
import { useToast } from "../ToastContext";
import { exportNotes } from "../ExportService";
import "./NoteList.css";

const TYPE_OPTIONS = ["ALL", "INFO", "REMINDER", "WARNING", "TEMPLATE"];
const SORT_OPTIONS = [
  { value: "smart",  label: "Smart" },
  { value: "recent", label: "Recently updated" },
  { value: "pinned", label: "Pinned first" },
  { value: "alpha",  label: "A → Z" },
];
const TYPE_CLASS = {
  INFO: "info", REMINDER: "reminder", WARNING: "warning", TEMPLATE: "template",
};

export default function NoteList() {
  const nav   = useNavigate();
  const toast = useToast();

  const [notes,        setNotes]        = useState([]);
  const [page,         setPage]         = useState(0);
  const [hasMore,      setHasMore]      = useState(true);
  const [loading,      setLoading]      = useState(false);
  const [total,        setTotal]        = useState(0);
  const [q,            setQ]            = useState("");
  const [typeFilter,   setTypeFilter]   = useState("ALL");
  const [sortBy,       setSortBy]       = useState("smart");
  const [viewMode,     setViewMode]     = useState("mine");
  const [sharedNotes,  setSharedNotes]  = useState([]);
  // Server-side multi-tag filter — Set of selected tag strings
  const [selectedTags, setSelectedTags] = useState(new Set());
  // All tags loaded once from server (not derived from page results)
  const [allTags,      setAllTags]      = useState([]);
  const [quickTitle,   setQuickTitle]   = useState("");
  const [quickBusy,    setQuickBusy]    = useState(false);

  const searchTimer = useRef(null);
  const bottomRef   = useRef(null);

  // Load the full tag list once on mount (and whenever the user creates/updates a note)
  const reloadTags = useCallback(() => {
    getNotesTags().then(t => setAllTags(t));
  }, []);

  useEffect(() => { reloadTags(); }, [reloadTags]);

  const loadPage = useCallback(async (nextPage, reset = false) => {
    if (loading && !reset) return;
    setLoading(true);
    const data = await getNotesSummaryPage({
      page: nextPage, size: 20, q, type: typeFilter, sort: sortBy,
      tags: [...selectedTags],
    });
    setNotes(prev => reset ? (data.content || []) : [...prev, ...(data.content || [])]);
    setPage(nextPage);
    setHasMore(!data.last);
    setTotal(data.totalElements || 0);
    setLoading(false);
  }, [q, typeFilter, sortBy, selectedTags]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadPage(0, true); }, [q, typeFilter, sortBy, selectedTags]); // eslint-disable-line

  useEffect(() => {
    if (viewMode !== "shared") return;
    getSharedNotes().then(d => setSharedNotes(Array.isArray(d) ? d : []));
  }, [viewMode]);

  useEffect(() => {
    if (!bottomRef.current) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && hasMore && !loading) loadPage(page + 1);
    }, { rootMargin: "200px" });
    obs.observe(bottomRef.current);
    return () => obs.disconnect();
  }, [hasMore, loading, page, loadPage]);

  const handleSearch = (v) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setQ(v); setSelectedTags(new Set()); }, 320);
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag); else next.add(tag);
      return next;
    });
  };

  const clearTags = () => setSelectedTags(new Set());

  const handleDelete = async (note) => {
    if (note.deleteProtected) { toast("This note is protected from deletion.", "warn"); return; }
    if (!window.confirm(`Delete "${note.title}"?`)) return;
    const res = await deleteNote(note.id);
    if (res && !res.ok) { toast("Delete failed.", "error"); return; }
    setNotes(prev => prev.filter(n => n.id !== note.id));
    setTotal(t => Math.max(0, t - 1));
    toast("Note deleted.", "info");
  };

  const handleShare = async (note) => {
    const input = window.prompt("Share with (usernames or emails, comma-separated):");
    if (!input) return;
    const tokens = input.split(",").map(s => s.trim()).filter(Boolean);
    const emails  = tokens.filter(t => t.includes("@"));
    const userIds = tokens.filter(t => !t.includes("@"));
    const sendEmail = window.confirm("Also send email notification?");
    const res = await shareNote(note.id, { userIds, emails, sendEmail });
    res.ok ? toast("Shared successfully.", "success") : toast("Share failed.", "error");
  };

  const handleEmail = async (note) => {
    const input = window.prompt("Email to (comma-separated):");
    if (!input) return;
    const tokens = input.split(",").map(s => s.trim()).filter(Boolean);
    const res = await emailNoteToRecipients(note.id, {
      emails:  tokens.filter(t => t.includes("@")),
      userIds: tokens.filter(t => !t.includes("@")),
    });
    res.ok ? toast("Email sent.", "success") : toast("Email failed.", "error");
  };

  const handleDuplicate = async (note) => {
    const created = await duplicateNote(note.id);
    if (!created?.id) { toast("Duplicate failed.", "error"); return; }
    const stub = { ...note, id: created.id, title: created.title, pinned: false, favorite: false };
    setNotes(prev => [stub, ...prev]);
    toast("Note duplicated.", "success");
  };

  const handleQuickCreate = async (e) => {
    e.preventDefault();
    if (!quickTitle.trim()) return;
    setQuickBusy(true);
    try {
      const auth = JSON.parse(localStorage.getItem("auth") || "{}");
      const res = await fetch("/api/note", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${auth.token}` },
        body: JSON.stringify({ title: quickTitle.trim(), noteType: "INFO", status: "ACTIVE" }),
      });
      if (res.ok) {
        const created = await res.json();
        setQuickTitle("");
        reloadTags();
        nav(`/notes/edit/${created.id}`);
      } else {
        toast("Could not create note.", "error");
      }
    } catch { toast("Could not create note.", "error"); }
    setQuickBusy(false);
  };

  const displayNotes = viewMode === "shared" ? sharedNotes : notes;
  // For "shared" view, derive tags client-side (shared notes aren't paginated the same way)
  const sharedTags = Array.from(new Set(sharedNotes.flatMap(n => n.tags || []))).sort();
  const tagList = viewMode === "shared" ? sharedTags : allTags;
  // In "mine" view filtering is server-side; in "shared" view apply client-side
  const visible = viewMode === "mine" ? displayNotes
    : selectedTags.size === 0 ? sharedNotes
    : sharedNotes.filter(n => (n.tags || []).some(t => selectedTags.has(t)));

  return (
    <div className="nl-wrap">
      <div className="nl-header">
        <div>
          <h2 className="nl-title">Notes</h2>
          {total > 0 && <span className="nl-count">{total.toLocaleString()} notes</span>}
        </div>
        <div className="nl-header-actions">
          <div className="nl-toggle">
            <button className={`nl-toggle-btn ${viewMode === "mine" ? "active" : ""}`}   onClick={() => setViewMode("mine")}>Mine</button>
            <button className={`nl-toggle-btn ${viewMode === "shared" ? "active" : ""}`} onClick={() => setViewMode("shared")}>Shared</button>
          </div>
          <button className="nl-ghost-btn" onClick={() => nav("/journal")}>Journal</button>
          {viewMode === "mine" && (
            <button className="nl-ghost-btn" onClick={() => exportNotes().catch(() => toast("Export failed", "error"))}>
              Export
            </button>
          )}
          {viewMode === "mine" && <button className="nl-new-btn" onClick={() => nav("new")}>+ New note</button>}
        </div>
      </div>

      {viewMode === "mine" && (
        <form className="nl-quick-create" onSubmit={handleQuickCreate}>
          <input
            className="nl-quick-input"
            placeholder="Quick note — type a title and press Enter"
            value={quickTitle}
            onChange={e => setQuickTitle(e.target.value)}
          />
          <button className="nl-quick-btn" type="submit" disabled={quickBusy || !quickTitle.trim()}>
            {quickBusy ? "…" : "Create"}
          </button>
        </form>
      )}

      <div className="nl-toolbar">
        <div className="nl-search-wrap">
          <span className="nl-search-icon">⌕</span>
          <input
            className="nl-search"
            placeholder="Search by title or tag…"
            defaultValue={q}
            onChange={e => handleSearch(e.target.value)}
          />
        </div>
        <select className="nl-select" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setSelectedTags(new Set()); }}>
          {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t === "ALL" ? "All types" : t}</option>)}
        </select>
        <select className="nl-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
          {SORT_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {tagList.length > 0 && (
        <div className="nl-tags-row">
          <button
            className={`nl-tag ${selectedTags.size === 0 ? "active" : ""}`}
            onClick={clearTags}
          >
            All
          </button>
          {tagList.map(tag => (
            <button
              key={tag}
              className={`nl-tag ${selectedTags.has(tag) ? "active" : ""}`}
              onClick={() => toggleTag(tag)}
            >
              {tag}
            </button>
          ))}
          {selectedTags.size > 0 && (
            <button className="nl-tag nl-tag-clear" onClick={clearTags}>
              Clear ({selectedTags.size})
            </button>
          )}
        </div>
      )}

      {selectedTags.size > 0 && (
        <div className="nl-filter-hint">
          Showing notes tagged with: {[...selectedTags].map(t => <strong key={t}>{t}</strong>).reduce((a, b) => [a, " or ", b])}
        </div>
      )}

      <div className="nl-grid">
        {visible.map(n => (
          <NoteCard
            key={n.id}
            note={n}
            isShared={viewMode === "shared"}
            activeTags={selectedTags}
            onEdit={() => viewMode === "mine"
              ? nav(`edit/${n.id}`)
              : nav("new", { state: { note: n, readOnly: true } })}
            onDelete={() => handleDelete(n)}
            onShare={() => handleShare(n)}
            onEmail={() => handleEmail(n)}
            onDuplicate={() => handleDuplicate(n)}
          />
        ))}
      </div>

      <div ref={bottomRef} className="nl-footer">
        {loading && <span className="nl-loading">Loading…</span>}
        {!loading && !hasMore && visible.length > 0 && (
          <span className="nl-end">All {total.toLocaleString()} notes loaded</span>
        )}
        {!loading && visible.length === 0 && (
          <div className="nl-empty">
            <p>No notes{q ? ` matching "${q}"` : selectedTags.size > 0 ? " for selected tags" : ""}.</p>
            {viewMode === "mine" && selectedTags.size === 0 && !q && (
              <button className="nl-new-btn" onClick={() => nav("new")}>Create your first note</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function NoteCard({ note, isShared, activeTags, onEdit, onDelete, onShare, onEmail, onDuplicate }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const typeClass = TYPE_CLASS[note.noteType] || "default";

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <article className={`nl-card ${note.pinned ? "nl-card-pinned" : ""}`} onClick={onEdit}>
      <div className="nl-card-top">
        <span className="nl-card-title">{note.title || "Untitled"}</span>
        <span className={`nl-card-badge nl-badge-${typeClass}`}>{note.noteType}</span>
      </div>

      {(note.pinned || note.favorite || note.secured || note.deleteProtected || note.hasAttachments || note.reminderAt) && (
        <div className="nl-card-flags">
          {note.pinned          && <span className="nl-flag" title="Pinned">📌</span>}
          {note.favorite        && <span className="nl-flag" title="Favorite">⭐</span>}
          {note.secured         && <span className="nl-flag" title="Encrypted">🔒</span>}
          {note.deleteProtected && <span className="nl-flag" title="Protected">🛡</span>}
          {note.hasAttachments  && <span className="nl-flag" title="Has attachments">📎</span>}
          {note.reminderAt      && <span className="nl-flag" title="Reminder set">⏰</span>}
        </div>
      )}

      {note.preview && <p className="nl-card-preview">{note.preview}</p>}

      {note.tags?.length > 0 && (
        <div className="nl-card-tags">
          {note.tags.slice(0, 6).map(t => (
            <span
              key={t}
              className={`nl-card-tag ${activeTags.has(t) ? "nl-card-tag-active" : ""}`}
            >
              {t}
            </span>
          ))}
          {note.tags.length > 6 && <span className="nl-card-tag nl-card-tag-more">+{note.tags.length - 6}</span>}
        </div>
      )}

      <div className="nl-card-footer" onClick={e => e.stopPropagation()}>
        <small className="nl-card-date">{note.updatedAt || note.createdDate || ""}</small>
        <div className="nl-card-actions" ref={menuRef}>
          <button className="nl-card-edit-btn" onClick={onEdit}>{isShared ? "View" : "Open"}</button>
          {!isShared && (
            <>
              <button className="nl-card-menu-btn" onClick={e => { e.stopPropagation(); setMenuOpen(p => !p); }}>⋯</button>
              {menuOpen && (
                <div className="nl-card-menu" onClick={e => e.stopPropagation()}>
                  <button onClick={() => { setMenuOpen(false); onShare(); }}>Share</button>
                  <button onClick={() => { setMenuOpen(false); onEmail(); }}>Email</button>
                  <button onClick={() => { setMenuOpen(false); onDuplicate(); }}>Duplicate</button>
                  <button className="nl-card-menu-danger" onClick={() => { setMenuOpen(false); onDelete(); }}>Delete</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </article>
  );
}
